import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { AnyAction } from 'redux';
import { put, select, takeEvery } from 'redux-saga/effects';
import { App } from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { loadsProductTable } from '../../app/angebot/children/tkv-v2.adapter';
import { IAppState } from '../appState';
import { ChildState, hasLoadedProdukt } from '../mappers/childStates';
import { notDeferredChildKeys } from '../models/angebotOffers';
import { childKeyOf, listedChildKeys } from '../models/childKeys';
import {
  ChildActionRequestedPayload,
  TgvAction,
  TgvActions,
  TgvPricingStatus,
} from '../tgvActions';
import { MAX_PARALLEL_PRICINGS, pricingRequestOf } from './pricingSchedulerSaga';
import { nextRequestId } from './requestIds';
import { dispatchToStoreOf, findStoreState, sharedDispatcher } from './sharedDispatcher';

export const PRICING_RELEVANT_ACTION_TYPES: ReadonlySet<string> = new Set([
  'SELBSTBETEILIGUNG_CHANGED',
  'THH_HUND_SELBSTBETEILIGUNG_CHANGED',
  'THH_PFERD_SELBSTBETEILIGUNG_CHANGED',
  'TARIF_CHECKED',
  'ZUSATZOPTION_CHECKED',
  'ZUSATZOPTION_UNCHECKED',
  'PREISE_REQUESTED',
  'THH_HUND_PREISE_REQUESTED',
  'THH_PFERD_PREISE_REQUESTED',
  'HEILBEHANDLUNGSSCHUTZ_CHANGED',
]);

const COVERAGE_TYPE_CHANGED = 'COVERAGE_TYPE_CHANGED';

export const isPricingRelevantAction = (action: AnyAction): boolean =>
  PRICING_RELEVANT_ACTION_TYPES.has(action.type);

const hasProduct = (childState: ChildState | undefined): boolean =>
  childState !== undefined && hasLoadedProdukt(childState);

const keysWithStatus = (state: IAppState, status: TgvPricingStatus): string[] =>
  notDeferredChildKeys(state.tiergruppe).filter(
    key => state.transientState.pricing[key]?.status === status,
  );

const mustHandOver = (state: IAppState, key: string, loadsTable: boolean): boolean => {
  const running = keysWithStatus(state, 'running');
  return (
    running.includes(key) ||
    state.transientState.pricing[key]?.status === 'failed' ||
    loadsTable ||
    running.length >= MAX_PARALLEL_PRICINGS ||
    keysWithStatus(state, 'queued').length > 0
  );
};

export function* watchChildActionForwarding(): SagaIterator {
  yield takeEvery(
    TgvActions.TGV_CHILD_ACTION_REQUESTED,
    safeSaga(childActionForwardingSaga, App.TGV),
  );
}

export function* childActionForwardingSaga(
  action: TgvAction<ChildActionRequestedPayload>,
): SagaIterator {
  const { child, inner } = action.payload;
  const state: IAppState = yield select();
  const { tiergruppe, transientState } = state;
  const key = childKeyOf(child);
  const registered = sharedDispatcher().findStoreRef(child.appId, child.angebotsId) !== undefined;
  if (key === null || !listedChildKeys(tiergruppe).includes(key) || !registered) {
    PS20Logger.info(
      'TGV_CHILD_ACTION_IGNORED',
      `Dropped ${inner.type} for ${child.appId}:${child.angebotsId}: the child is not listed or has no registered store`,
      App.TGV,
    );
    return;
  }
  if (
    transientState.children.pendingDeletion.includes(key) ||
    transientState.children.creating.includes(key)
  ) {
    PS20Logger.info(
      'TGV_CHILD_ACTION_IGNORED',
      `Dropped ${inner.type} for ${key}: the child is pending deletion or still being created`,
      App.TGV,
    );
    return;
  }
  if (transientState.takeOverRunning) {
    PS20Logger.info(
      'TGV_CHILD_ACTION_IGNORED',
      `Dropped ${inner.type} for ${key}: the group is being taken over into the dashboard`,
      App.TGV,
    );
    return;
  }
  if (isPricingRelevantAction(inner)) {
    const childState = findStoreState(child.appId, child.angebotsId);
    if (!hasProduct(childState)) {
      PS20Logger.info(
        'TGV_CHILD_ACTION_IGNORED',
        `Dropped ${inner.type} for ${key}: the child has no loaded product`,
        App.TGV,
      );
      return;
    }
    if (mustHandOver(state, key, loadsProductTable(child.appId, childState))) {
      yield put(new TgvActions().childPricingForwardQueued(key, inner));
      return;
    }
    const requestId = nextRequestId(key);
    yield put(new TgvActions().childPricingForwarded(key, requestId));
    dispatchToStoreOf(child.appId, child.angebotsId, pricingRequestOf(inner, requestId));
    return;
  }
  if (inner.type === COVERAGE_TYPE_CHANGED) {
    yield put(new TgvActions().childPricingForwarded(key, nextRequestId(key), true));
    dispatchToStoreOf(child.appId, child.angebotsId, { ...inner, tgvManaged: true });
    return;
  }
  dispatchToStoreOf(child.appId, child.angebotsId, inner);
}
